import {
  Component,
  ViewChild,
  ViewEncapsulation,
  OnInit,
  Input,
  Inject,
} from "@angular/core";
import {
  FormGroup,
  FormBuilder,
  FormArray,
  Validators,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { ApiService } from "../../../lib/api.service";
import { LogService } from "../../../lib/log.service";
import { ModalService } from "../../../lib/modal.service";
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Course, Chapter, Video, Attachment, AudioCourse } from "../../../models/course";
import * as XLSX from "xlsx";
import { AnalyticEvent } from "app/lib/analytic-event";

interface DailyQuote {
  category_id: any;
  quote_en: any;
  quote_fr: any;
  quote_es: any;
}

@Component({
  selector: 'app-daily-series-create',
  templateUrl: './daily-series-create.component.html',
  styleUrls: ['./daily-series-create.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DailySeriesCreateComponent implements OnInit {
  createDailySeriesForm: FormGroup;
  courseEditForm: FormGroup;
  courseKey: string;
  title: string;
  mode: string;
  course: AudioCourse;
  isLoaded: boolean = false;
  errors:string = "";
  file: File = undefined;
  fileError: string = "";
  arrayBuffer: any;
  dailySeries: DailyQuote[] = [];
  courseDailyId: any;
  groupTitle = [];
  submitButtonPressed: boolean = false;
  categoryList: any;
  constructor( private translate: TranslateService,
    private formBuilder: FormBuilder,
    private api: ApiService,
    private log: LogService,
    private modalService: ModalService,
    public dialogRef: MatDialogRef<DailySeriesCreateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any) {
      this.mode = data?.data.mode
      dialogRef.disableClose = true;      
      this.createCourseEditForm(new AudioCourse({}));
      if (this.mode === "update") {
        this.courseDailyId = data.data.data;
        this.title = "Edit this Daily Series";
        this.mode = "update";
      } else if (this.mode === "create"){
        this.groupTitle = data.data.groups;
        this.title = "Create a Daily Series";
        this.mode = "create";
        this.isLoaded = true;
      }
      this.createDailySeriesForm = this.formBuilder.group({
        title: ["", [Validators.required]],
        excelFile: ["", [Validators.required]]
      });
     }

     createCourseEditForm(data: any){
      this.courseEditForm = this.formBuilder.group({
        id: [data.id || null],
        category: [data.categoryid || "", [Validators.required]],
        quotesen: [data.quotesen || "", [Validators.required]],
        quotesfr: [data.quotesfr || "", [Validators.required]],
        quoteses: [data.quoteses || "", [Validators.required]]
      });
    }

     get f() {
      return this.createDailySeriesForm.controls;
    }

    get g() {
      return this.courseEditForm.controls;
    }

  ngOnInit(): void {
    if (this.courseDailyId) {
      this.api
        .get(`admin/series/dailyseries/` + this.courseDailyId)
        .subscribe(
          (result: any) => {
            this.course = new AudioCourse(result[0]);
            this.createCourseEditForm(this.course);
            this.isLoaded = true;
          },
          (error: any) => {
            this.log.error(
              "Error getting organizations. " + error.message
            );
          }
        );
         this.api
        .get("series/categories")
        .subscribe(
          (result: any) => {
            this.categoryList = result;
            this.isLoaded = true;
          },
          (error: any) => {
            this.log.error(
              "Error getting organizations. " + error.message
            );
          }
        );
    }
  }

  incomingfile(event) {
		this.file = event.target.files[0];
	}

  doSave() {
    if (this.mode === "update") {
      this.course = new AudioCourse(this.courseEditForm.value);
      this.submitButtonPressed = true;
      if (!this.courseEditForm.valid) {
        this.courseEditForm.markAllAsTouched();
        return;
      }
      this.errors = "";
      if (this.errors) {
        return;
      }
      this.isLoaded = false;
      let payload = {
        category: this.courseEditForm.value.category,
        title_en: this.courseEditForm.value.quotesen, 
        title_fr: this.courseEditForm.value.quotesfr,  
        title_es: this.courseEditForm.value.quoteses, 
      }
      this.api
        .post(
          "series/dailyseries/update/" + this.courseDailyId,
          payload
        )
        .subscribe(
          (data: any) => {
            this.modalService.showAlert(
              "Success",
              "Daily Series has been updated"
            );
            this.isLoaded = true;
            this.dialogRef.close(this.course);
          },
          (error: any) => {
            this.isLoaded = true;
            this.modalService.showAlert(
              "Error",
              "Something went wrong. " + error.message
            );
          }
        );
    } else {
      this.fileError = "";
      if (!this.file) {
        this.fileError = "Please upload a valid file";
        return;
      }
      this.uploadSpreadsheet();
    }
  }

  uploadSpreadsheet() {
      this.dailySeries = [];
      this.fileError = "";  
      let fileReader = new FileReader();
      fileReader.readAsArrayBuffer(this.file);
      fileReader.onload = (e) => {
        this.arrayBuffer = fileReader.result;
        var data = new Uint8Array(this.arrayBuffer);
        var arr = new Array();
        for (var i = 0; i != data.length; ++i)
          arr[i] = String.fromCharCode(data[i]);
        var bstr = arr.join("");
        var workbook = XLSX.read(bstr, { type: "binary" });
        var first_sheet_name = workbook.SheetNames[0];
        var worksheet = workbook.Sheets[first_sheet_name];  

        const headerRowRaw = XLSX.utils.sheet_to_json(worksheet, { header: 1 })[0];
        const headerRow: string[] = Array.isArray(headerRowRaw)
          ? headerRowRaw.map((cell) => cell?.toString().trim() || "")
          : [];

        const requiredHeaders = ["Category ID", "English", "French", "Spanish"];
        const missingHeaders = requiredHeaders.filter(h => !headerRow.includes(h));

        if (missingHeaders.length > 0) {
          this.fileError = `Missing required columns / Title of columns: ${missingHeaders.join(", ")}`;
          return;
        }

        const titleToCheck = this.createDailySeriesForm.value.title.trim();
        const alreadyExists = this.groupTitle.some(group => group.Name.trim().toLowerCase() === titleToCheck.toLowerCase());

        if (alreadyExists) {
          this.fileError = "Title already exists.";
          return;
        }

        const importedData: any = XLSX.utils.sheet_to_json(worksheet, { raw: true });
        this.log.debug(importedData);
        for (let i: number = 0; i < importedData.length; i++) {
          const content = {
            category_id: importedData[i]["Category ID"],
            quote_en: importedData[i]["English"],
            quote_fr: importedData[i]["French"],
            quote_es: importedData[i]["Spanish"]
          }
          this.dailySeries.push(content)
        }
        if (this.dailySeries.length === 0) {
          this.fileError = "Invalid file or no items found"!;
          return;
        }  
        this.importResources();
      };
    }

    importResources() {
        this.isLoaded = false;
        let payload = {
          title: this.createDailySeriesForm.value.title,
          daily_series: this.dailySeries,
        }
        this.api
          .post(
            `series/dailyseries/import`,payload
          )
          .subscribe(
            (data: any) => {
              this.modalService.showAlert(
                "Success",
                "Daily Series has been imported"
              );
              this.isLoaded = true;
              this.dialogRef.close(data);
            },
            (error: any) => {
              this.modalService.showAlert(
                "Error",
                "Something went wrong. " + error.message
              );
              // this.log.event(AnalyticEvent.event.resourceImported);
              delete this.file;
              this.isLoaded = true;
            }
          );
      }

  onClose() {
    this.dialogRef.close();
  }
}
